import { useState, useEffect } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "./firebase";
import LoginPage from "./auth/LoginPage";
import InvitationPage from "./auth/InvitationPage";
import LauncherPage from "./dashboard/LauncherPage";
import AdminPage from "./admin/AdminPage";
import QrRedirectPage from "./qr/QrRedirectPage";

const FONT = "'Plus Jakarta Sans', sans-serif";

function App() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState("dashboard");
  const isInvitationRoute = window.location.pathname.startsWith("/invitacion");
  // /q/{id} — destino de los códigos QR ploteados en los equipos.
  // Maneja su propio ciclo de auth, igual que la página de invitación.
  const isQrRoute = window.location.pathname.startsWith("/q/");

  useEffect(() => {
    if (isInvitationRoute || isQrRoute) { setLoading(false); return; }
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        import('./services/auditLog').then(({ logAudit }) => logAudit('login', { app: 'platform', email: currentUser.email }));
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        if (userDoc.exists()) {
          const profile = userDoc.data();
          setUserProfile(profile);
          window.__currentUser = {
            uid: currentUser.uid,
            email: currentUser.email,
            displayName: currentUser.displayName,
            tenant_id: profile.active_tenant_id || profile.tenant_id,
          };
        }
      } else {
        setUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, [isInvitationRoute, isQrRoute]);

  if (isInvitationRoute) return <InvitationPage />;
  if (isQrRoute) return <QrRedirectPage />;
  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT }}>
      <p style={{ color: "#64748b" }}>Cargando...</p>
    </div>
  );
  if (!user) return <LoginPage />;

  const canAdmin =
    userProfile?.role_platform === "superadmin" ||
    userProfile?.role_platform === "tenant_admin";

  return (
    <div>
      {canAdmin && (
        <div style={sx.topNav}>
          <div style={sx.navLeft}>
            <svg width="18" height="18" viewBox="0 0 40 40" style={{ marginRight: 4 }}>
              <line x1="20" y1="7" x2="20" y2="20" stroke="#374151" strokeWidth="1.5"/>
              <line x1="33" y1="20" x2="20" y2="20" stroke="#374151" strokeWidth="1.5"/>
              <line x1="20" y1="33" x2="20" y2="20" stroke="#374151" strokeWidth="1.5"/>
              <line x1="7" y1="20" x2="20" y2="20" stroke="#374151" strokeWidth="1.5"/>
              <circle cx="20" cy="7" r="2.5" fill="#E03131"/>
              <circle cx="33" cy="20" r="2.5" fill="#E03131"/>
              <circle cx="20" cy="33" r="2.5" fill="#E03131"/>
              <circle cx="7" cy="20" r="2.5" fill="#E03131"/>
              <circle cx="20" cy="20" r="4" fill="#E03131"/>
            </svg>
            <button
              style={{ ...sx.navBtn, ...(currentPage === "dashboard" ? sx.navBtnActive : {}) }}
              onClick={() => setCurrentPage("dashboard")}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
              </svg>
              Dashboard
            </button>
            <button
              style={{ ...sx.navBtn, ...(currentPage === "admin" ? sx.navBtnActive : {}) }}
              onClick={() => setCurrentPage("admin")}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
              Administración
            </button>
          </div>
        </div>
      )}
      {currentPage === "dashboard" && <LauncherPage user={user} />}
      {currentPage === "admin" && canAdmin && <AdminPage user={user} userProfile={userProfile} />}
    </div>
  );
}

const sx = {
  topNav: {
    backgroundColor: "#0B0F14", padding: "0 28px", display: "flex",
    alignItems: "center", justifyContent: "space-between", height: 44,
    fontFamily: FONT,
  },
  navLeft: {
    display: "flex", alignItems: "center", gap: 4,
  },
  navBtn: {
    padding: "6px 14px", backgroundColor: "transparent", color: "#94a3b8",
    border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13,
    fontWeight: 600, fontFamily: FONT, display: "flex", alignItems: "center",
    gap: 6, transition: "all 0.15s",
  },
  navBtnActive: {
    backgroundColor: "rgba(224, 49, 49, 0.15)", color: "#fff",
  },
};

export default App;